import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import Modal from "../../../../components/base/Modal";
import { UserForm, DRIVER_ROLE_ID, type UserFormData } from "./UserForm";
import api from "../../../../utils/api";
import {
  useCompaniesStore,
  type Company,
} from "../../../../store/admin/companiesStore";
import { ADMIN_RESELLER_ROLE_ID } from "../../../../hooks/useIsReseller";
import type { Role } from "../../../../store/admin/rolesStore";
import type { Status } from "../../../../store/admin/statusesStore";
import { allDevicesQueryOptions } from "../../../../features/devices/queries";
import type { User } from "../../../../store/admin/usersStore";
import type { ThemeClasses } from "../../../../types/components";
import {
  DEFAULT_NOTIFICATION_PREFS,
  parseNotificationPrefsResponse,
} from "../../../../features/users/notificationPrefs";

interface UserAddModalProps {
  isOpen: boolean;
  isEdit: boolean;
  editUser?: User | null;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  companies: Company[];
  roles: Role[];
  statuses: Status[];
  companiesLoading: boolean;
  rolesLoading: boolean;
  statusesLoading: boolean;
  loadingSubmit: boolean;
  onClose: () => void;
  onSubmit: (data: UserFormData) => void;
}

export default function UserAddModal({
  isOpen,
  isEdit,
  editUser,
  companiesLoading,
  rolesLoading,
  statusesLoading,
  isDarkMode,
  themeClasses,
  companies,
  roles,
  statuses,
  loadingSubmit,
  onClose,
  onSubmit,
}: UserAddModalProps) {
  const { t } = useTranslation("adminSections");
  const [loadingDriverData, setLoadingDriverData] = useState(false);

  // Desformatear teléfono de E.164 a formato local (solo dígitos sin código de país)
  const formatPhoneToLocal = (phone: string | undefined): string => {
    if (!phone || phone.trim() === "") return "";

    // Eliminar espacios, guiones, paréntesis y otros caracteres no numéricos excepto +
    let cleaned = phone.replace(/[^0-9+]/g, "");

    // Si tiene +52 al inicio, quitarlo (México)
    if (cleaned.startsWith("+52")) {
      return cleaned.substring(3);
    }

    // Si tiene + al inicio, quitarlo
    if (cleaned.startsWith("+")) {
      return cleaned.substring(1);
    }

    // Si tiene 52 al inicio pero sin +, quitarlo
    if (cleaned.startsWith("52") && cleaned.length === 12) {
      return cleaned.substring(2);
    }

    // Devolver el número tal cual
    return cleaned;
  };

  // Obtener el company_id del usuario actual desde localStorage
  const userCompanyId = localStorage.getItem("userCompanyId") || "";

  // Verificar si es reseller por ROL (no por compañía)
  const isReseller =
    (localStorage.getItem("userRoleId") || "") === ADMIN_RESELLER_ROLE_ID;

  // Obtener la compañía seleccionada en el header (selector global)
  const headerSelectedCompanyId = useCompaniesStore((state) => state.companyId);

  // Determinar qué company_id usar por defecto:
  // - Admin (non-reseller): siempre su propia compañía (auto-asignada)
  // - Reseller + compañía seleccionada en header: usar la del header
  // - Reseller + sin compañía en header: vacío (debe seleccionar en el modal)
  const effectiveCompanyId = isReseller
    ? headerSelectedCompanyId || ""
    : userCompanyId;

  // Mostrar el dropdown de compañías en el modal solo si:
  // - El usuario es reseller Y no hay compañía seleccionada en el header
  // - Admin (non-reseller) NUNCA ve el dropdown, su compañía se auto-asigna
  const showCompanySelector = isReseller && !headerSelectedCompanyId;

  // Obtener el status ACTIVE por defecto para usuarios
  const defaultStatusId =
    statuses.find((s) => s.name.toUpperCase() === "ACTIVE")?.id || "";

  const form = useForm<UserFormData>({
    mode: "all",
    defaultValues: {
      username: "",
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      phone: "",
      address: "",
      userType: "",
      company_id: effectiveCompanyId,
      role_id: "",
      status_id: defaultStatusId,
      available_devices: [],
      // Driver fields
      licenseId: "",
      licenseExp: "",
      licenceExpedition: "",
      contactsEmergency: [],
      userDev: false,
      canUseSG: false,
      bio: "",
      location: "",
      website: "",
      notification_prefs: DEFAULT_NOTIFICATION_PREFS,
    },
  });

  // Helper to format Firestore timestamp / ISO string to YYYY-MM-DD for <input type="date">
  const formatTimestampToDate = (timestamp: any): string => {
    if (!timestamp) return "";
    if (typeof timestamp === "string") {
      return timestamp.includes("T") ? timestamp.split("T")[0] : timestamp;
    }
    if (timestamp._seconds) {
      return new Date(timestamp._seconds * 1000).toISOString().split("T")[0];
    }
    return "";
  };

  // Resetear formulario cuando cambia isEdit o editUser
  useEffect(() => {
    if (isEdit && editUser) {
      // Encontrar el rol del usuario para obtener el userType
      const userRole = roles.find((r) => r.id === editUser.role_id);
      const isDriverRole = editUser.role_id === DRIVER_ROLE_ID;

      const baseValues: UserFormData = {
        username: editUser.username || "",
        first_name: editUser.first_name || "",
        last_name: editUser.last_name || "",
        email: editUser.email || "",
        password: "", // No mostrar la contraseña actual
        phone: formatPhoneToLocal(editUser.phone),
        address: editUser.address || "",
        userType: userRole?.name || "",
        company_id: editUser.company_id || "",
        role_id: editUser.role_id || "",
        status_id: editUser.status_id || "active",
        available_devices: (editUser as any).available_devices || [],
        licenseId: "",
        licenseExp: "",
        licenceExpedition: "",
        contactsEmergency: [],
        userDev: (editUser as any).userDev === true,
        canUseSG: (editUser as any).canUseSG === true,
        bio: (editUser as any).bio || "",
        location: (editUser as any).location || "",
        website: (editUser as any).website || "",
        notification_prefs: DEFAULT_NOTIFICATION_PREFS,
      };

      const loadPrefs = api
        .get("/user/notification-prefs", { params: { userID: editUser.id } })
        .then((response) =>
          parseNotificationPrefsResponse(response.data),
        )
        .catch((err) => {
          console.error("Error fetching notification prefs:", err);
          return DEFAULT_NOTIFICATION_PREFS;
        });

      if (isDriverRole) {
        // Cargar driver primero para evitar errores de validación en campos requeridos vacíos
        setLoadingDriverData(true);
        Promise.all([
          api.get("/driverAsociated", { params: { userId: editUser.id } }),
          loadPrefs,
        ])
          .then(([driverResponse, prefs]) => {
            const driverData =
              driverResponse.data?.data?.data ?? driverResponse.data?.data;
            if (driverData) {
              baseValues.licenseId = driverData.licenseId || "";
              baseValues.licenseExp = formatTimestampToDate(driverData.licenseExp);
              baseValues.licenceExpedition = formatTimestampToDate(
                driverData.licenceExpedition,
              );
              baseValues.contactsEmergency = driverData.contactsEmergency || [];
            }
            baseValues.notification_prefs = prefs;
          })
          .catch((err) => {
            console.error("Error fetching driver data:", err);
          })
          .finally(() => {
            form.reset(baseValues);
            setLoadingDriverData(false);
            setTimeout(() => form.trigger(), 0);
          });
      } else {
        setLoadingDriverData(true);
        loadPrefs
          .then((prefs) => {
            baseValues.notification_prefs = prefs;
          })
          .finally(() => {
            form.reset(baseValues);
            setLoadingDriverData(false);
            setTimeout(() => form.trigger(), 0);
          });
      }
    } else {
      form.reset({
        username: "",
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        phone: "",
        address: "",
        userType: "",
        company_id: effectiveCompanyId,
        role_id: "",
        status_id: defaultStatusId,
        available_devices: [],
        // Driver fields
        licenseId: "",
        licenseExp: "",
        licenceExpedition: "",
        contactsEmergency: [],
        userDev: false,
        canUseSG: false,
        bio: "",
        location: "",
        website: "",
        notification_prefs: DEFAULT_NOTIFICATION_PREFS,
      });
    }
  }, [isEdit, editUser, roles, form, effectiveCompanyId, defaultStatusId]);

  // Actualizar role_id cuando cambia userType
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "userType" && value.userType) {
        const selectedRole = roles.find((r) => r.name === value.userType);
        if (selectedRole) {
          form.setValue("role_id", selectedRole.id, { shouldValidate: true });
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form, roles]);

  // Trigger validation when companies load (for non-reseller users with pre-set company_id)
  useEffect(() => {
    if (companies.length > 0 && !companiesLoading) {
      form.trigger("company_id");
    }
  }, [companies.length, companiesLoading, form]);
  const formCompanyId = form.watch("company_id");
  const devicesQuery = useQuery(allDevicesQueryOptions(formCompanyId || null));
  const devices = devicesQuery.data ?? [];
  const devicesLoading = devicesQuery.isFetching;
  const devicesInitialLoading = devicesQuery.isLoading;

  const prevCompanyRef = useRef<string | null>(null);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "company_id" && value.company_id) {
        if (
          prevCompanyRef.current !== null &&
          prevCompanyRef.current !== value.company_id
        ) {
          form.setValue("available_devices", []);
        }
        prevCompanyRef.current = value.company_id;
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  useEffect(() => {
    if (isOpen) {
      prevCompanyRef.current = form.getValues("company_id") || null;
    }
  }, [isOpen, isEdit, editUser, form]);

  if (!isOpen) return null;

  const handleFormSubmit = (data: UserFormData) => {
    // Asegurarse de que role_id esté establecido
    const selectedRole = roles.find((r) => r.name === data.userType);
    if (selectedRole) {
      data.role_id = selectedRole.id;
    }
    onSubmit(data);
  };

  return (
    <Modal
      title={isEdit ? t("users.modal.editTitle") : t("users.modal.addTitle")}
      themeClasses={themeClasses}
      onClose={onClose}
      content={
        <div className="w-[500px] max-w-full pr-2">
          {loadingDriverData ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
              <span className={`text-sm ${themeClasses.textSecondary}`}>
                {t("users.form.loadingData", "Cargando información del usuario...")}
              </span>
            </div>
          ) : (
            <UserForm
              form={form}
              isEdit={isEdit}
              loadingSubmit={loadingSubmit}
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
              companies={companies}
              roles={roles}
              statuses={statuses}
              devices={devices}
              companiesLoading={companiesLoading}
              rolesLoading={rolesLoading}
              statusesLoading={statusesLoading}
              devicesLoading={devicesLoading}
              devicesInitialLoading={devicesInitialLoading}
              showCompanySelector={showCompanySelector}
              onSubmit={handleFormSubmit}
              onCancel={onClose}
            />
          )}
        </div>
      }
    />
  );
}
